import { useState, useEffect } from 'react';
import { 
  Card, CardContent, Typography, Box, Button, Alert, Chip, 
  Grid, LinearProgress, IconButton, Collapse
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { api, authenticatedFetch } from '../utils/api';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

function EnhancedAIInsights() {
  const [insights, setInsights] = useState(null);
  const [dailyRecommendations, setDailyRecommendations] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    insights: true,
    recommendations: true,
    patterns: false
  });

  useEffect(() => {
    fetchAIInsights();
    fetchDailyRecommendations();
    fetchPatterns();
  }, []);

  const fetchAIInsights = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(api.aiEnhancedAnalysis, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (response.ok) {
        setInsights(data);
      } else {
        setError(data.error || 'Failed to get AI insights');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyRecommendations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(api.aiDailyRecommendations, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (response.ok) {
        setDailyRecommendations(data.recommendations || []);
      }
    } catch (err) {
      console.error('Error fetching recommendations:', err);
    }
  };

  const fetchPatterns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(api.aiPatterns, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (response.ok) {
        setPatterns(data.patterns || []);
      }
    } catch (err) {
      console.error('Error fetching patterns:', err);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getActionIcon = (action) => {
    const icons = {
      'exercise': '💪',
      'sleep': '😴',
      'nutrition': '🥗',
      'hydration': '💧',
      'stress': '🧘',
      'mood': '😊'
    };
    return icons[action] || '💡';
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
          🤖 AI Health Coach
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />} 
          onClick={fetchAIInsights}
          disabled={loading}
        >
          {loading ? 'Analyzing...' : 'Refresh Insights'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Today's Priority Recommendations */}
      <Card sx={{ mb: 3, borderLeft: 4, borderColor: 'primary.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              🎯 Today's Priority Actions
            </Typography>
            <IconButton onClick={() => toggleSection('recommendations')}>
              {expandedSections.recommendations ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={expandedSections.recommendations}>
            {dailyRecommendations.length > 0 ? (
              <Grid container spacing={2}>
                {dailyRecommendations.map((rec, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6">{getActionIcon(rec.category)}</Typography>
                        <Chip 
                          label={rec.priority} 
                          size="small" 
                          color={getPriorityColor(rec.priority)}
                        />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {rec.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {rec.description}
                      </Typography>
                      {rec.progress && (
                        <Box sx={{ mt: 2 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={rec.progress} 
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {rec.progress}% complete today
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No recommendations yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete some check-ins to receive personalized daily recommendations!
                </Typography>
              </Box>
            )}
          </Collapse>
        </CardContent>
      </Card>

      {/* AI Health Analysis */}
      {insights && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                📊 AI Health Analysis
              </Typography>
              <IconButton onClick={() => toggleSection('insights')}>
                {expandedSections.insights ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            
            <Collapse in={expandedSections.insights}>
              {/* Health Score */}
              {insights.healthScore && (
                <Box sx={{ mb: 3, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Overall Health Score</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={insights.healthScore.score} 
                        sx={{ height: 12, borderRadius: 6 }}
                        color={insights.healthScore.score >= 80 ? 'success' : insights.healthScore.score >= 60 ? 'primary' : 'warning'}
                      />
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {insights.healthScore.score}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {insights.healthScore.description}
                  </Typography>
                </Box>
              )}

              {/* Key Insights */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Key Insights</Typography>
              <Box sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, mb: 3 }}>
                {insights.analysis}
              </Box>

              {/* Trend Indicators */}
              {insights.trends && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>Health Trends</Typography>
                  <Grid container spacing={2}>
                    {insights.trends.map((trend, index) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                          {trend.direction === 'up' ? (
                            <TrendingUpIcon color="success" sx={{ fontSize: 30 }} />
                          ) : (
                            <TrendingDownIcon color="error" sx={{ fontSize: 30 }} />
                          )}
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
                            {trend.metric}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {trend.change}
                          </Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* Pattern Recognition */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              🔍 Discovered Patterns
            </Typography>
            <IconButton onClick={() => toggleSection('patterns')}>
              {expandedSections.patterns ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={expandedSections.patterns}>
            {patterns.length > 0 ? (
              <Grid container spacing={2}>
                {patterns.map((pattern, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Alert 
                      severity={pattern.type === 'positive' ? 'success' : pattern.type === 'negative' ? 'warning' : 'info'}
                      icon={<LightbulbIcon />}
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {pattern.title}
                      </Typography>
                      <Typography variant="body2">
                        {pattern.description}
                      </Typography>
                      {pattern.confidence && (
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          Confidence: {pattern.confidence}%
                        </Typography>
                      )}
                    </Alert>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color="text.secondary">
                Keep logging check-ins to discover patterns in your health data!
              </Typography>
            )}
          </Collapse>
        </CardContent>
      </Card>
    </Box>
  );
}

export default EnhancedAIInsights;
